// src/components/CalendarGrid/WeekView.jsx
import { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import styles from './WeekView.module.css';
import { isToday, dateToDecimalHour, eventsOverlap, layoutEventsForDay, formatTime } from './utils.js';

// Map event.type → cal design token colours (VEN-421)
const TYPE_COLORS = {
  available:   { bg: 'var(--cal-color-available-bg)',   border: 'var(--cal-color-available)',   text: 'var(--cal-color-available)'   },
  booking:     { bg: 'var(--cal-color-booking-bg)',     border: 'var(--cal-color-booking)',     text: 'var(--cal-color-booking)'     },
  proposal:    { bg: 'var(--cal-color-proposal-bg)',    border: 'var(--cal-color-proposal)',    text: 'var(--cal-color-proposal)'    },
  unavailable: { bg: 'var(--cal-color-unavailable-bg)', border: 'var(--cal-color-unavailable)', text: 'var(--cal-color-unavailable)' },
};

function getEventColors(event) {
  if (event.type && TYPE_COLORS[event.type]) return TYPE_COLORS[event.type];
  const colour = event.colour || 'var(--color-accent)';
  return {
    bg:     `color-mix(in srgb, ${colour} 15%, var(--cal-surface-base, var(--bg-primary)))`,
    border: colour,
    text:   colour,
  };
}

const HOUR_HEIGHT = 64; // px per hour
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatHourLabel(hour) {
  if (hour === 0) return '12am';
  if (hour < 12) return `${hour}am`;
  if (hour === 12) return '12pm';
  return `${hour - 12}pm`;
}

function getWeekStart(date, weekStartsOn = 1) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day - weekStartsOn + 7) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDays(currentDate, weekStartsOn = 1) {
  const start = getWeekStart(currentDate, weekStartsOn);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function hourToY(hour, startHour) {
  return (hour - startHour) * HOUR_HEIGHT;
}

function isAllDayEvent(event) {
  if (event.allDay) return true;
  const start = new Date(event.start);
  const end = new Date(event.end);
  return (
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    end.getHours() === 0 &&
    end.getMinutes() === 0 &&
    end - start >= 86400000
  );
}

/**
 * WeekView — time-grid week layout for CalendarGrid (Stream 1, Ticket 1.2).
 *
 * Props:
 *   currentDate          Date               — any date in the target week
 *   events               Array              — [{ id, start, end, type?, label?, colour?, allDay? }]
 *   onSlotClick          fn(Date)           — fired when an empty slot is clicked
 *   onEventClick         fn(evt)            — fired when an event block is clicked
 *   onEventContextMenu   fn(evt, {x,y})     — fired on right-click of an event block
 *   onDragSelect         fn(start, end)     — fired when user drags to select a time range
 *   renderEvent          fn(evt)            — optional custom render for event blocks
 *   businessHours        { start: 'HH:MM', end: 'HH:MM' }
 *   weekStartsOn         0|1               — 0=Sunday, 1=Monday (default)
 */
const WeekView = ({
  currentDate = new Date(),
  events = [],
  onSlotClick,
  onEventClick,
  onEventContextMenu,
  onDragSelect,
  renderEvent,
  businessHours = { start: '08:00', end: '18:00' },
  weekStartsOn = 1,
}) => {
  const gridRef = useRef(null);
  const nowIndicatorRef = useRef(null);

  // Drag-to-select state (visual overlay only; logic uses ref to avoid stale closures)
  const [dragOverlay, setDragOverlay] = useState(null); // { colIdx, topPx, heightPx }
  const dragState = useRef(null); // { colIdx, day, startY, startHour }
  const wasDrag = useRef(false);  // true when mouseup followed a real drag (suppresses click)

  const hourStart = parseInt(businessHours.start.split(':')[0], 10);
  const hourEnd = parseInt(businessHours.end.split(':')[0], 10);
  const totalHours = hourEnd - hourStart;
  const gridHeight = totalHours * HOUR_HEIGHT;

  const weekDays = useMemo(
    () => getWeekDays(currentDate, weekStartsOn),
    [currentDate, weekStartsOn]
  );

  const dayEventData = useMemo(() => {
    return weekDays.map(day => {
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);

      const dayEvents = events.filter(event => {
        const s = new Date(event.start);
        const e = new Date(event.end);
        return s < dayEnd && e > dayStart;
      });

      const allDay = dayEvents.filter(isAllDayEvent);
      const timed = dayEvents.filter(e => !isAllDayEvent(e));

      const timedWithPositions = timed
        .map(event => {
          const start = new Date(event.start);
          const end = new Date(event.end);

          const startHour = Math.max(dateToDecimalHour(start), hourStart);

          // If event ends on a different day, clamp to end of visible range
          const endDecimal =
            end.toDateString() !== start.toDateString()
              ? hourEnd
              : dateToDecimalHour(end);

          const endHour = Math.min(endDecimal, hourEnd);

          return {
            ...event,
            startHour,
            endHour: Math.max(startHour + 0.25, endHour), // minimum 15-min height
          };
        })
        .filter(e => e.startHour < hourEnd && e.endHour > hourStart);

      return {
        day,
        allDay,
        timedLayout: layoutEventsForDay(timedWithPositions),
      };
    });
  }, [weekDays, events, hourStart, hourEnd]);

  const getNowY = useCallback(() => {
    const now = new Date();
    const nowHour = dateToDecimalHour(now);
    if (nowHour < hourStart || nowHour > hourEnd) return null;
    return hourToY(nowHour, hourStart);
  }, [hourStart, hourEnd]);

  // Scroll to current time (or start of business hours) on mount
  useEffect(() => {
    if (!gridRef.current) return;
    const y = getNowY();
    gridRef.current.scrollTop = y !== null ? Math.max(0, y - 80) : 0;
  }, [getNowY]);

  // Keep the now-indicator position fresh (updates every minute)
  useEffect(() => {
    const update = () => {
      if (!nowIndicatorRef.current) return;
      const y = getNowY();
      nowIndicatorRef.current.style.display = y !== null ? 'block' : 'none';
      if (y !== null) nowIndicatorRef.current.style.top = `${y}px`;
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [getNowY]);

  const handleColumnClick = useCallback(
    (day, e) => {
      // Skip if a drag just completed (mouseup fires before click; wasDrag tracks it)
      if (wasDrag.current) { wasDrag.current = false; return; }
      if (e.button !== 0) return;
      if (!onSlotClick) return;
      if (e.target.closest('[data-event]')) return;

      // e.clientY - colRect.top gives distance from the column's actual top
      // (getBoundingClientRect reflects scroll position, so no extra adjustment needed)
      const colRect = e.currentTarget.getBoundingClientRect();
      const relY = e.clientY - colRect.top;
      const clickedHour = hourStart + relY / HOUR_HEIGHT;
      const rounded = Math.floor(clickedHour * 2) / 2; // snap to 30-min slots
      const dt = new Date(day);
      dt.setHours(Math.floor(rounded), (rounded % 1) * 60, 0, 0);
      onSlotClick(dt);
    },
    [onSlotClick, hourStart]
  );

  // Drag-to-select: mousedown begins a potential drag
  const handleColumnMouseDown = useCallback(
    (day, colIdx, e) => {
      if (e.button !== 0) return;
      if (e.target.closest('[data-event]')) return;
      if (!onDragSelect) return;

      const colRect = e.currentTarget.getBoundingClientRect();
      const relY = e.clientY - colRect.top;
      const startHour = hourStart + relY / HOUR_HEIGHT;

      dragState.current = { colIdx, day, startY: relY, startHour, colRect };
      wasDrag.current = false;
      setDragOverlay(null);

      // Prevent text selection while dragging
      e.preventDefault();
    },
    [onDragSelect, hourStart]
  );

  // Attach mousemove/mouseup to document so drag works outside the column
  useEffect(() => {
    if (!onDragSelect) return;

    function handleMouseMove(e) {
      const ds = dragState.current;
      if (!ds) return;

      const colEl = gridRef.current?.querySelector(`[data-col="${ds.colIdx}"]`);
      if (!colEl) return;
      const colRect = colEl.getBoundingClientRect();
      const relY = Math.max(0, Math.min(e.clientY - colRect.top, gridHeight));
      const moved = Math.abs(relY - ds.startY);
      if (moved < 8) return; // threshold — ignore tiny movements (click vs drag)

      wasDrag.current = true;
      const top = Math.min(ds.startY, relY);
      const heightPx = Math.abs(relY - ds.startY);
      setDragOverlay({ colIdx: ds.colIdx, topPx: top, heightPx: Math.max(heightPx, 16) });
    }

    function handleMouseUp(e) {
      const ds = dragState.current;
      dragState.current = null;

      if (!ds || !dragOverlay) {
        setDragOverlay(null);
        return;
      }

      setDragOverlay(null);

      // Compute start/end dates from drag
      const colEl = gridRef.current?.querySelector(`[data-col="${ds.colIdx}"]`);
      if (!colEl) return;
      const colRect = colEl.getBoundingClientRect();
      const endRelY = Math.max(0, Math.min(e.clientY - colRect.top, gridHeight));

      const rawStartHour = ds.startHour;
      const rawEndHour = hourStart + endRelY / HOUR_HEIGHT;

      // Snap both ends to 30-min slots
      const snapStart = Math.floor(Math.min(rawStartHour, rawEndHour) * 2) / 2;
      const snapEnd = Math.ceil(Math.max(rawStartHour, rawEndHour) * 2) / 2;

      if (snapEnd - snapStart < 0.25) return; // ignore tiny drags

      const startDt = new Date(ds.day);
      startDt.setHours(Math.floor(snapStart), (snapStart % 1) * 60, 0, 0);
      const endDt = new Date(ds.day);
      endDt.setHours(Math.floor(snapEnd), (snapEnd % 1) * 60, 0, 0);

      onDragSelect(startDt, endDt);
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [onDragSelect, hourStart, gridHeight, dragOverlay]);

  const hasAnyAllDay = dayEventData.some(d => d.allDay.length > 0);

  return (
    <div className={styles.weekView}>
      {/* ── Day header row ── */}
      <div className={styles.headerRow}>
        <div className={styles.timeGutterHeader} />
        {dayEventData.map(({ day, allDay }, idx) => (
          <div
            key={idx}
            className={`${styles.dayHeader}${isToday(day) ? ` ${styles.dayHeaderToday}` : ''}`}
          >
            <div className={styles.dayName}>{DAY_NAMES[day.getDay()]}</div>
            <div
              className={`${styles.dayNumber}${isToday(day) ? ` ${styles.dayNumberToday}` : ''}`}
            >
              {day.getDate()}
            </div>
            {hasAnyAllDay && (
              <div className={styles.allDayRow}>
                {allDay.map(event => (
                  <div
                    key={event.id}
                    className={styles.allDayEvent}
                    style={{ background: event.colour || 'var(--color-amber)' }}
                    onClick={() => onEventClick?.(event)}
                    title={event.label || ''}
                  >
                    {event.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Scrollable time grid ── */}
      <div className={styles.gridScroll} ref={gridRef}>
        <div className={styles.gridInner} style={{ height: `${gridHeight}px` }}>
          {/* Hour labels */}
          <div className={styles.timeGutter}>
            {Array.from({ length: totalHours }, (_, i) => (
              <div
                key={i}
                className={styles.hourLabel}
                style={{ top: `${i * HOUR_HEIGHT}px` }}
              >
                {formatHourLabel(hourStart + i)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {dayEventData.map(({ day, timedLayout }, colIdx) => (
            <div
              key={colIdx}
              data-col={colIdx}
              className={`${styles.dayColumn}${isToday(day) ? ` ${styles.dayColumnToday}` : ''}`}
              onClick={e => handleColumnClick(day, e)}
              onMouseDown={e => handleColumnMouseDown(day, colIdx, e)}
              role="presentation"
            >
              {/* Horizontal hour lines */}
              {Array.from({ length: totalHours }, (_, i) => (
                <div
                  key={i}
                  className={styles.hourLine}
                  style={{ top: `${i * HOUR_HEIGHT}px` }}
                />
              ))}

              {/* Half-hour dashed lines */}
              {Array.from({ length: totalHours }, (_, i) => (
                <div
                  key={`h${i}`}
                  className={styles.halfHourLine}
                  style={{ top: `${i * HOUR_HEIGHT + HOUR_HEIGHT / 2}px` }}
                />
              ))}

              {/* Current-time indicator (today column only) */}
              {isToday(day) && (
                <div ref={nowIndicatorRef} className={styles.nowIndicator} />
              )}

              {/* Drag-to-select overlay (only rendered on the active column) */}
              {dragOverlay && dragOverlay.colIdx === colIdx && (
                <div
                  className={styles.dragOverlay}
                  style={{ top: dragOverlay.topPx, height: dragOverlay.heightPx }}
                  aria-hidden="true"
                />
              )}

              {/* Timed event blocks */}
              {timedLayout.map(({ event, col, totalCols }) => {
                const top = hourToY(event.startHour, hourStart);
                const height = Math.max(
                  (event.endHour - event.startHour) * HOUR_HEIGHT - 2,
                  20
                );
                const widthPct = 100 / totalCols;
                const leftPct = (col / totalCols) * 100;

                const { bg, border, text } = getEventColors(event);
                const blockStyle = {
                  top,
                  height,
                  width: `${widthPct}%`,
                  left: `${leftPct}%`,
                  background: bg,
                  borderLeftColor: border,
                  color: text,
                };

                const handleContextMenu = onEventContextMenu
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEventContextMenu(event, { x: e.clientX, y: e.clientY });
                    }
                  : undefined;

                if (renderEvent) {
                  return (
                    <div
                      key={event.id}
                      data-event=""
                      className={styles.eventWrapper}
                      style={blockStyle}
                      onClick={e => {
                        e.stopPropagation();
                        onEventClick?.(event);
                      }}
                      onContextMenu={handleContextMenu}
                    >
                      {renderEvent(event)}
                    </div>
                  );
                }

                return (
                  <div
                    key={event.id}
                    data-event=""
                    className={styles.event}
                    style={blockStyle}
                    onClick={e => {
                      e.stopPropagation();
                      onEventClick?.(event);
                    }}
                    onContextMenu={handleContextMenu}
                    title={event.label || ''}
                  >
                    <div className={styles.eventLabel}>{event.label}</div>
                    {height > 28 && (
                      <div className={styles.eventTime}>
                        {formatTime(new Date(event.start))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeekView;
